import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { TokenService } from 'src/app/core/token/token.service';
import { IConsumoContratoCliente } from 'src/app/interfaces/consumo-contratos-cliente.interface';
import {
  formatarPercentualConsumo,
  ILinhaHorasTickets,
  MODALIDADES_HORAS,
  montarLinhasConsumo,
  consumoItemIlimitado,
  tituloConsumoContrato,
} from 'src/app/pages/tickets/editar-ticket/consumo-contrato-ticket/consumo-contrato.utils';
import { ClienteContatoService } from 'src/app/services/cliente-contato.service';
import { ClienteEnderecoService } from 'src/app/services/cliente-endereco.service';
import { ClienteService } from 'src/app/services/cliente.service';
import { environment } from 'src/environments/environment';

const LINHAS_POR_PAGINA = 6;

export interface IContratoBloco extends IConsumoContratoCliente {
  linhasHoras: ILinhaHorasTickets[];
  linhasTickets: ILinhaHorasTickets[];
}

@Component({
  selector: 'app-modal-infos-cliente-chamado',
  templateUrl: './modal-infos-cliente-chamado.component.html',
  styleUrls: ['./modal-infos-cliente-chamado.component.scss']
})
export class ModalInfosClienteChamadoComponent implements OnInit, OnDestroy {
  @Input() clienteData;
  infosCliente;
  clienteEndereco;
  clienteContato;
  /** Tabela de horas com 8 colunas fixas: Categorias + as 7 modalidades. */
  readonly modalidadesHoras = MODALIDADES_HORAS;
  contratos: IContratoBloco[] = [];
  carregandoContratos = true;
  erroContratos = false;
  paginas: { [chave: string]: number } = {};
  selector: string;
  newSubs: Subscription;
  subsContratos: Subscription;
  permissao = {
    EditarCliente: this.tokenService.checkPermissions('@EditarCliente')
  };

  constructor(
    private clienteService: ClienteService,
    private clienteEnderecoService: ClienteEnderecoService,
    private clienteContatoService: ClienteContatoService,
    public modal: NgbActiveModal,
    private toastr: ToastrService,
    public translate: TranslateService,
    private elemRef: ElementRef,
    private route: Router,
    private tokenService: TokenService
    ) {
      this.selector = elemRef.nativeElement.tagName.toLowerCase();
    }

  ngOnInit(): void {
    document.getElementsByTagName(this.selector)[0]['style']['zoom'] = '0.85';
    this.getInfosCliente();
    this.getContratoCliente();
  }

  ngOnDestroy(): void {
    this.newSubs?.unsubscribe();
    this.subsContratos?.unsubscribe();
  }

  goToCliente(id) {
    window.open(`registration/customers/${id}`, '_blank');
    //this.modal.close(id)
  }

  getInfosCliente(): void {
    this.newSubs = this.clienteService.getById(this.clienteData)
      .subscribe(results => {
        if(results) {
          this.infosCliente = results.cliente;   
          //console.log(this.infosCliente);                 
        }
        this.getEnderecoCliente();
      }, error => {})
  }

  getEnderecoCliente(): void {
    this.newSubs = this.clienteEnderecoService.getEnderecoCliente(this.clienteData)
      .subscribe(results => {
        if(results) {
          this.clienteEndereco = results.clienteEndereco
        }
        this.getContatoCliente();
      }, error => {})
  }

  getContatoCliente(): void {
    this.newSubs = this.clienteContatoService.getContatoCliente(this.clienteData)
      .subscribe(results => {
        if(results) {
          this.clienteContato = results.clienteContato;
        }
      }, error => {})
  }

  getContratoCliente(): void {
    this.carregandoContratos = true;
    this.erroContratos = false;
    this.subsContratos?.unsubscribe();
    this.subsContratos = this.clienteService.getConsumoContratos(this.clienteData)
      .subscribe(results => {
        this.contratos = (results?.contratos || []).map(contrato => this.montarContratoBloco(contrato));
        this.paginas = {};
        this.carregandoContratos = false;
      }, error => {
        this.contratos = [];
        this.erroContratos = true;
        this.carregandoContratos = false;
      })
  }

  private montarContratoBloco(contrato: IConsumoContratoCliente): IContratoBloco {
    const horas = contrato.horas || [];
    const tickets = contrato.tickets || [];
    const todasCategorias = this.translate.instant('CONSUMO_CONTRATO_TODAS_CATEGORIAS');
    const montarLinhas = (categorias, preencher: (linha: ILinhaHorasTickets, categoria) => void): ILinhaHorasTickets[] =>
      montarLinhasConsumo(categorias, todasCategorias, preencher);

    return {
      ...contrato,
      linhasHoras: montarLinhas(horas, (linha, categoria) => (categoria.modalidades || []).forEach(modalidade => linha.horas[modalidade.tipo] = modalidade)),
      linhasTickets: montarLinhas(tickets, (linha, categoria) => linha.ticket = categoria),
      dispositivos: [...(contrato.dispositivos || [])].sort((a, b) => this.nomeDispositivo(a).localeCompare(this.nomeDispositivo(b), undefined, { sensitivity: 'base', numeric: true })),
      outros: [...(contrato.outros || [])].sort((a, b) => (a.servico || '').localeCompare(b.servico || '', undefined, { sensitivity: 'base', numeric: true }))
    };
  }

  linhasPagina<T>(lista: T[], chave: string): T[] {
    const inicio = this.paginaAtual(chave) * LINHAS_POR_PAGINA;
    return lista.slice(inicio, inicio + LINHAS_POR_PAGINA);
  }

  paginaAtual(chave: string): number {
    return this.paginas[chave] || 0;
  }

  totalPaginas(lista: any[]): number {
    return Math.ceil(lista.length / LINHAS_POR_PAGINA);
  }

  exibirPaginacao(lista: any[]): boolean {
    return lista.length > LINHAS_POR_PAGINA;
  }

  mudarPagina(chave: string, lista: any[], direcao: number): void {
    const proxima = this.paginaAtual(chave) + direcao;
    if (proxima >= 0 && proxima < this.totalPaginas(lista)) {
      this.paginas[chave] = proxima;
    }
  }

  textoPaginacao(chave: string, lista: any[]): string {
    const inicio = this.paginaAtual(chave) * LINHAS_POR_PAGINA;
    return this.translate.instant('MOSTRANDO_PAGINACAO', {
      from: inicio + 1,
      to: Math.min(inicio + LINHAS_POR_PAGINA, lista.length),
      total: lista.length
    });
  }

  formatarPercentual(percentual: number | null): string {
    return formatarPercentualConsumo(percentual);
  }

  itemIlimitado(item): boolean {
    return consumoItemIlimitado(item);
  }

  tituloConsumo(consumido: string | number, item): string {
    return tituloConsumoContrato(consumido, item, this.translate.instant('ILIMITADO'));
  }

  nomeDispositivo(item): string {
    const codigo = item.tipo_dispositivo_codigo || item.tipo_dispositivo;
    if (!codigo) {
      return item.tipo_dispositivo;
    }
    const chave = `TIPO_DISPOSITIVO_${String(codigo).normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '_').toUpperCase()}`;
    const traduzido = this.translate.instant(chave);
    return traduzido === chave ? item.tipo_dispositivo : traduzido;
  }

  /** Quantidade no formato `contratado/atual`; `---` quando o tipo não está contratado. */
  quantidadeDispositivo(item): string {
    return !item.quantidade_contratada && !item.quantidade_atual ? '---' : `${item.quantidade_contratada}/${item.quantidade_atual}`;
  }

  copyToClipboard(valor): void {
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = valor;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.toastr.show(this.translate.instant('COPIADO_SUCESSO'), "", environment.toastrConfig("success"));
  }
}
